// src/pages/Settings.js
// Settings page for practice configuration

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts';
import { supabase } from '../lib/supabaseClient';
import { Settings as SettingsIcon, Users, Database, CreditCard, LogOut, CheckCircle, XCircle, RefreshCw, Zap, Bell, Clock, Shield, Wifi, Eye, EyeOff, Building2, Plus, Mail, Trash2, Landmark, ExternalLink, AlertCircle, Phone, Edit2, Link } from 'lucide-react';
import { savePMSConfig, testPMSConnection, getAvailablePMSSystems, PMS_SYSTEMS } from '../services/pmsSyncService';
import * as awsApi from '../services/awsApiService';
import { demoPayers } from '../data/demoData';

const ORG_ROLES = ['billing_admin', 'dso_admin', 'super_admin'];

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, practice, signOut } = useAuth();
  const isDemoMode = (process.env.REACT_APP_MODE || '').trim() === 'demo' || (typeof window !== 'undefined' && window.location.hostname === 'demo.flowrcmdental.com');
  const isOrgUser = ORG_ROLES.includes(practice?.role) || isDemoMode;
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);

  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const [activeSection, setActiveSection] = useState(() => searchParams.get('tab') || 'pms');

  // Sync activeSection when tab query param changes (e.g. during demo tour navigation)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveSection(tab);
  }, [searchParams]);

  // Clearinghouse settings state
  const [chConfig, setChConfig] = useState({
    clearinghouse: 'office_ally',
    host: '',
    port: '22',
    username: '',
    password: '',
    upload_directory: '',
  });
  const [chLoading, setChLoading] = useState(false);
  const [chSaving, setChSaving] = useState(false);
  const [chTesting, setChTesting] = useState(false);
  const [chSuccess, setChSuccess] = useState('');
  const [chError, setChError] = useState('');
  const [chTestResult, setChTestResult] = useState(null);
  const [showChPassword, setShowChPassword] = useState(false);

  // Open Dental integration state
  const [odKey, setOdKey] = useState('');
  const [odSaved, setOdSaved] = useState(false);
  const [odLastSync, setOdLastSync] = useState(null);
  const [odTesting, setOdTesting] = useState(false);
  const [odSyncing, setOdSyncing] = useState(false);
  const [odTestResult, setOdTestResult] = useState(null);
  const [odSyncResult, setOdSyncResult] = useState(null);
  const [odSaving, setOdSaving] = useState(false);
  const [odSaveError, setOdSaveError] = useState(null);
  const [showOdKey, setShowOdKey] = useState(false);

  // Payers state
  const [payers, setPayers] = useState([]);
  const [payersLoading, setPayersLoading] = useState(false);
  const [payerSearch, setPayerSearch] = useState('');
  const [showPayerModal, setShowPayerModal] = useState(false);
  const [editingPayer, setEditingPayer] = useState(null);
  const [payerForm, setPayerForm] = useState({ payer_name: '', payer_id: '', appeal_method: 'fax', appeal_fax: '', appeal_email: '', appeal_portal_url: '', appeal_notes: '' });
  const [payerSaving, setPayerSaving] = useState(false);
  const [payerError, setPayerError] = useState('');
  const [verifyingPayerId, setVerifyingPayerId] = useState(null);
  const [confirmVerify, setConfirmVerify] = useState(null);
  const [payerMsg, setPayerMsg] = useState('');

  // Availity credentials state
  const [availityConfig, setAvailityConfig] = useState({ username: '', password: '' });
  const [availitySaving, setAvailitySaving] = useState(false);
  const [availitySuccess, setAvailitySuccess] = useState('');
  const [availityError, setAvailityError] = useState('');
  const [showAvailityPassword, setShowAvailityPassword] = useState(false);

  // Stripe Connect / Bank Deposits state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectOnboarding, setConnectOnboarding] = useState(false);
  const [connectDashboardLoading, setConnectDashboardLoading] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connectSuccess, setConnectSuccess] = useState('');

  // Organization state
  const [org, setOrg] = useState(null);
  const [orgPractices, setOrgPractices] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [orgSuccess, setOrgSuccess] = useState('');
  const [showAddPractice, setShowAddPractice] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [newPractice, setNewPractice] = useState({ name: '', npi: '', contact_email: '', phone: '' });
  const [inviteForm, setInviteForm] = useState({ email: '', invited_role: 'staff', practice_id: '' });
  const [orgSaving, setOrgSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', npi: '', ein: '', phone: '', address: '', city: '', state: '', zip: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  const [selectedPMS, setSelectedPMS] = useState(PMS_SYSTEMS.CSV_IMPORT);
  const [pmsConfig, setPmsConfig] = useState({
    apiUrl: '',
    apiKey: '',
    username: '',
    password: '',
  });
  const pmsSystems = getAvailablePMSSystems();

  // Automation settings state
  const [automationSettings, setAutomationSettings] = useState({
    autoAppealEnabled: true,
    remindersEnabled: true,
    agingFlagEnabled: true,
    minBalance: 5,
    reminderDays: [7, 30, 60],
    eraPostingMode: 'full',
    autoSubmitPreauthEnabled: false,
    benefitsVerificationEnabled: true,
    claimRobotEnabled: true,
    predWorkflowMode: 'submit_and_pull',
    automationEmailEnabled: true,
    autoPatientPaymentLinks: true,
  });
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [automationSuccess, setAutomationSuccess] = useState('');

  // Billing exceptions state
  const [exceptions, setExceptions] = useState({ allowedDuplicateCodes: [], suppressFlags: [], customNotes: '' });
  const [excLoading, setExcLoading] = useState(false);
  const [excSaving, setExcSaving] = useState(false);
  const [excSuccess, setExcSuccess] = useState('');
  const [excError, setExcError] = useState('');
  const [newDupCode, setNewDupCode] = useState('');
  const [newSuppressFlag, setNewSuppressFlag] = useState('');

  // Load PMS config from Lambda (handles both Open Dental API and bot-based systems)
  useEffect(() => {
    const loadPMSConfig = async () => {
      try {
        // Try bot config first (WaveOrtho, Eaglesoft, Dentrix Ascend, Carestream)
        const botResult = await awsApi.getPmsBotConfig();
        if (botResult.success && botResult.data?.system) {
          setSelectedPMS(botResult.data.system);
          setPmsConfig(prev => ({
            ...prev,
            username: botResult.data.config?.hasUsername ? '••••••••' : '',
            password: botResult.data.config?.hasPassword ? '••••••••' : '',
          }));
          setOdLastSync(botResult.data.lastSync || null);
          return;
        }
        // Fall back to Open Dental API config
        const odResult = await awsApi.getOpenDentalConfig();
        if (odResult.success && odResult.data) {
          setSelectedPMS(odResult.data.system || PMS_SYSTEMS.CSV_IMPORT);
          setPmsConfig(prev => ({
            ...prev,
            apiUrl: odResult.data.config?.apiUrl || '',
            apiKey: odResult.data.config?.apiKey || '',
          }));
          setOdLastSync(odResult.data.lastSync || null);
        }
      } catch (err) {}
    };
    loadPMSConfig();
  }, []);

  const handleSyncNow = async () => {
    setOdSyncing(true);
    setOdSyncMessage(null);
    try {
      const result = await awsApi.syncOpenDentalData('both');
      if (result.success) {
        const patients = result.results?.patients;
        const claims = result.results?.claims;
        const patientCount = patients ? (patients.inserted + patients.updated) : 0;
        const claimCount = claims ? (claims.inserted + claims.updated) : 0;
        setOdLastSync(new Date().toISOString());
        setOdSyncMessage({ type: 'success', text: `Sync complete — ${patientCount} patients, ${claimCount} claims updated.` });
      } else {
        setOdSyncMessage({ type: 'error', text: result.error || 'Sync failed. Check your connection.' });
      }
    } catch (err) {
      setOdSyncMessage({ type: 'error', text: err.message });
    } finally {
      setOdSyncing(false);
    }
  };

  // Load automation settings
  useEffect(() => {
    const loadAutomation = async () => {
      setAutomationLoading(true);
      try {
        const result = await awsApi.getAutomationSettings();
        if (result.success && result.data?.settings) {
          setAutomationSettings(prev => ({ ...prev, ...result.data.settings }));
        }
      } catch (err) {}
      finally { setAutomationLoading(false); }
    };
    loadAutomation();
  }, []);

  const handleSaveAutomation = async () => {
    setAutomationSaving(true);
    setAutomationSuccess('');
    try {
      const result = await awsApi.saveAutomationSettings(automationSettings);
      if (result.success) {
        setAutomationSuccess('Automation settings saved.');
        setTimeout(() => setAutomationSuccess(''), 3000);
      }
    } catch (err) {}
    finally { setAutomationSaving(false); }
  };

  // Load billing exceptions
  useEffect(() => {
    const loadExceptions = async () => {
      setExcLoading(true);
      try {
        const result = await awsApi.getBillingExceptions();
        if (result.success && result.data?.exceptions) {
          setExceptions(prev => ({ ...prev, ...result.data.exceptions }));
        }
      } catch (err) {}
      finally { setExcLoading(false); }
    };
    loadExceptions();
  }, []);

  const handleSaveExceptions = async () => {
    setExcSaving(true);
    setExcSuccess('');
    setExcError('');
    try {
      const result = await awsApi.saveBillingExceptions(exceptions);
      if (result.success) {
        setExcSuccess('Exception rules saved.');
        setTimeout(() => setExcSuccess(''), 3000);
      } else {
        setExcError(result.error || 'Failed to save');
      }
    } catch (err) {
      setExcError(err.message);
    } finally { setExcSaving(false); }
  };

  const handleTestConnection = async () => {
    if (selectedPMS === PMS_SYSTEMS.CSV_IMPORT) {
      setTestResult({ success: true, message: 'CSV import is always available!' });
      return;
    }

    setTesting(true);
    setTestResult(null);
    setError('');

    try {
      const result = await testPMSConnection(selectedPMS, pmsConfig);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const BOT_PMS_SYSTEMS = ['waveortho', 'eaglesoft', 'dentrix-ascend', 'carestream'];

  const handleSavePMSConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setTestResult(null);

    try {
      let result;
      if (BOT_PMS_SYSTEMS.includes(selectedPMS)) {
        if (!pmsConfig.username || !pmsConfig.password || pmsConfig.password === '••••••••') {
          throw new Error('Username and password are required');
        }
        result = await awsApi.savePmsBotConfig(selectedPMS, pmsConfig.username, pmsConfig.password, pmsConfig.portalUrl);
      } else {
        result = await savePMSConfig(practice?.id, selectedPMS, pmsConfig);
      }

      if (result.success) {
        setSuccess('PMS configuration saved successfully!');
      } else {
        throw new Error(result.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemoData = async (reset = false) => {
    setSeeding(true);
    setSeedResult(null);
    setError('');

    try {
      const result = await awsApi.seedDemoData(reset);
      if (result.success) {
        setSeedResult(result.data);
      } else {
        setError(result.error || 'Failed to seed demo data');
      }
    } catch (err) {
      setError(err.message || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  // Load clearinghouse settings
  useEffect(() => {
    const loadCH = async () => {
      setChLoading(true);
      try {
        const result = await awsApi.getClearinghouseSettings();
        if (result.success && result.data && Object.keys(result.data).length > 0) {
          setChConfig(prev => ({ ...prev, ...result.data }));
        }
      } catch (err) {}
      finally { setChLoading(false); }
    };
    loadCH();
  }, []);

  // Load Availity config
  useEffect(() => {
    const loadAvailityConfig = async () => {
      try {
        const result = await awsApi.getAvailityConfig();
        if (result.success && result.data?.username) {
          setAvailityConfig({ username: result.data.username, password: '••••••••' });
        }
      } catch (err) {}
    };
    loadAvailityConfig();
  }, []);

  const loadPayers = async () => {
    setPayersLoading(true);
    if (isDemoMode) {
      const filtered = payerSearch
        ? demoPayers.filter(p => p.payer_name.toLowerCase().includes(payerSearch.toLowerCase()))
        : demoPayers;
      setPayers(filtered);
      setPayersLoading(false);
      return;
    }
    const result = await awsApi.getPayers(payerSearch);
    setPayers(result.payers || []);
    setPayersLoading(false);
  };

  const openAddPayer = () => {
    setEditingPayer(null);
    setPayerForm({ payer_name: '', payer_id: '', appeal_method: 'fax', appeal_fax: '', appeal_email: '', appeal_portal_url: '', appeal_notes: '' });
    setPayerError('');
    setShowPayerModal(true);
  };

  const openEditPayer = (p) => {
    setEditingPayer(p);
    setPayerForm({ payer_name: p.payer_name, payer_id: p.payer_id || '', appeal_method: p.appeal_method || 'fax', appeal_fax: p.appeal_fax || '', appeal_email: p.appeal_email || '', appeal_portal_url: p.appeal_portal_url || '', appeal_notes: p.appeal_notes || '' });
    setPayerError('');
    setShowPayerModal(true);
  };

  const handleSavePayer = async () => {
    if (!payerForm.payer_name.trim()) { setPayerError('Payer name is required'); return; }
    if (isDemoMode) { setShowPayerModal(false); setPayerMsg('Demo mode — changes not saved.'); setTimeout(() => setPayerMsg(''), 3000); return; }
    setPayerSaving(true);
    setPayerError('');
    const result = editingPayer
      ? await awsApi.updatePayer(editingPayer.id, payerForm)
      : await awsApi.createPayer(payerForm);
    setPayerSaving(false);
    if (result.success) {
      setShowPayerModal(false);
      if (result.verification_reset) setPayerMsg('Payer updated. Contact changed — re-verification required before auto-send resumes.');
      else setPayerMsg(editingPayer ? 'Payer updated.' : 'Payer added.');
      loadPayers();
      setTimeout(() => setPayerMsg(''), 5000);
    } else {
      setPayerError(result.error || 'Save failed');
    }
  };

  const handleDeletePayer = async (id) => {
    if (isDemoMode) { setPayerMsg('Demo mode — changes not saved.'); setTimeout(() => setPayerMsg(''), 3000); return; }
    if (!window.confirm('Delete this payer? This cannot be undone.')) return;
    await awsApi.deletePayer(id);
    loadPayers();
  };

  const handleVerifyPayer = async (payer) => {
    if (isDemoMode) {
      setConfirmVerify(null);
      setPayers(prev => prev.map(p => p.id === payer.id ? { ...p, human_verified: true, human_verified_by: 'demo-admin' } : p));
      setPayerMsg(`${payer.payer_name} verified. 1 held appeal sent automatically. (Demo)`);
      setTimeout(() => setPayerMsg(''), 6000);
      return;
    }
    setVerifyingPayerId(payer.id);
    const result = await awsApi.verifyPayer(payer.id, true);
    setVerifyingPayerId(null);
    setConfirmVerify(null);
    if (result.success) {
      const released = result.heldAppealsReleased || 0;
      setPayerMsg(`${payer.payer_name} verified.${released > 0 ? ` ${released} held appeal${released > 1 ? 's' : ''} sent automatically.` : ''}`);
      loadPayers();
      setTimeout(() => setPayerMsg(''), 6000);
    } else {
      setPayerMsg('Verification failed: ' + (result.error || 'Unknown error'));
    }
  };

  const handleUnverifyPayer = async (id) => {
    if (isDemoMode) { setPayers(prev => prev.map(p => p.id === id ? { ...p, human_verified: false, human_verified_by: null } : p)); return; }
    await awsApi.verifyPayer(id, false);
    loadPayers();
  };

  const handleSaveAvailityConfig = async () => {
    setAvailitySaving(true);
    setAvailitySuccess('');
    setAvailityError('');
    try {
      const result = await awsApi.saveAvailityConfig(availityConfig);
      if (result.success) {
        setAvailitySuccess('Availity credentials saved.');
        setTimeout(() => setAvailitySuccess(''), 3000);
      } else {
        setAvailityError(result.error || 'Failed to save credentials');
      }
    } catch (err) {
      setAvailityError(err.message);
    } finally { setAvailitySaving(false); }
  };

  const handleSaveClearinghouse = async () => {
    setChSaving(true);
    setChSuccess('');
    setChError('');
    setChTestResult(null);
    try {
      const result = await awsApi.saveClearinghouseSettings(chConfig);
      if (result.success) {
        setChSuccess('Clearinghouse settings saved.');
        setTimeout(() => setChSuccess(''), 3000);
      } else {
        setChError(result.error || 'Failed to save settings');
      }
    } catch (err) {
      setChError(err.message);
    } finally { setChSaving(false); }
  };

  const handleTestClearinghouse = async () => {
    setChTesting(true);
    setChTestResult(null);
    setChError('');
    try {
      const result = await awsApi.testClearinghouseConnection();
      if (result.success) {
        setChTestResult({ success: true, message: result.data?.message || 'Connection successful!' });
      } else {
        setChTestResult({ success: false, message: result.error || 'Connection failed' });
      }
    } catch (err) {
      setChTestResult({ success: false, message: err.message });
    } finally { setChTesting(false); }
  };

  // Load org data
  useEffect(() => {
    if (!isOrgUser) return;
    const loadOrg = async () => {
      setOrgLoading(true);
      const [orgRes, practicesRes] = await Promise.all([awsApi.getOrg(), awsApi.getOrgPractices()]);
      if (orgRes.success) setOrg(orgRes.data);
      if (practicesRes.success) setOrgPractices(practicesRes.data || []);
      setOrgLoading(false);
    };
    loadOrg();
  }, [isOrgUser]);

  const handleAddPractice = async () => {
    if (!newPractice.name) return setOrgError('Practice name is required');
    setOrgSaving(true); setOrgError(''); setOrgSuccess('');
    const res = await awsApi.createOrgPractice(newPractice);
    setOrgSaving(false);
    if (res.success) {
      setOrgPractices(prev => [...prev, res.data]);
      setNewPractice({ name: '', npi: '', contact_email: '', phone: '' });
      setShowAddPractice(false);
      setOrgSuccess('Practice added successfully.');
      setTimeout(() => setOrgSuccess(''), 3000);
    } else {
      setOrgError(res.error || 'Failed to add practice');
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email) return setOrgError('Email is required');
    setOrgSaving(true); setOrgError(''); setOrgSuccess('');
    const res = await awsApi.inviteOrgUser(inviteForm);
    setOrgSaving(false);
    if (res.success) {
      setInviteForm({ email: '', invited_role: 'staff', practice_id: '' });
      setShowInviteUser(false);
      setOrgSuccess(`Invitation sent to ${inviteForm.email}`);
      setTimeout(() => setOrgSuccess(''), 4000);
    } else {
      setOrgError(res.error || 'Failed to send invite');
    }
  };

  useEffect(() => {
    if (activeSection === 'payers') loadPayers();
    if (activeSection === 'integrations') loadOdConfig();
  }, [activeSection]); // eslint-disable-line

  const loadOdConfig = async () => {
    if (isDemoMode) { setOdSaved(false); return; }
    try {
      const res = await awsApi.getOpenDentalConfig();
      if (res.success) {
        setOdSaved(res.configured || false);
        setOdLastSync(res.last_sync || null);
      }
    } catch (err) { /* non-fatal */ }
  };

  const handleOdSave = async () => {
    if (!odKey.trim()) return;
    if (isDemoMode) { setOdSaved(true); setOdTestResult({ success: true, message: 'Demo mode — connected to Open Dental demo office', patientCount: 48 }); return; }
    setOdSaving(true); setOdSaveError(null);
    const res = await awsApi.saveOpenDentalConfig(odKey.trim());
    setOdSaving(false);
    if (res.success) { setOdSaved(true); setOdKey(''); }
    else { setOdSaveError(res.error || 'Save failed — check that you are an admin for this practice.'); }
  };

  const handleOdTest = async () => {
    const key = odKey.trim() || null;
    if (!key && !odSaved) return;
    if (isDemoMode) { setOdTestResult({ success: true, message: 'Connected to Open Dental demo office', patientCount: 48 }); return; }
    setOdTesting(true); setOdTestResult(null);
    const res = await awsApi.testOpenDentalConnection(key);
    setOdTesting(false);
    setOdTestResult(res);
  };

  const handleOdSync = async () => {
    if (isDemoMode) { setOdSyncResult({ success: true, patients: 48, claims: 127, errors: [] }); return; }
    setOdSyncing(true); setOdSyncResult(null);
    const res = await awsApi.syncOpenDental();
    setOdSyncing(false);
    setOdSyncResult(res);
    if (res.success) setOdLastSync(new Date().toISOString());
  };

  // Load Connect status when banking tab is active
  useEffect(() => {
    if (activeSection !== 'banking') return;
    const load = async () => {
      setConnectLoading(true);
      setConnectError('');
      try {
        const result = await awsApi.connectStatus();
        if (result.success) setConnectStatus(result.data);
      } catch (err) {}
      finally { setConnectLoading(false); }
    };
    load();
  }, [activeSection]);

  // Also handle return from Stripe onboarding (?tab=banking&connected=true)
  useEffect(() => {
    if (searchParams.get('connected') === 'true' && activeSection === 'banking') {
      setConnectSuccess('Bank account connected successfully!');
      setTimeout(() => setConnectSuccess(''), 5000);
    }
  }, [searchParams, activeSection]);

  const handleConnectOnboard = async () => {
    setConnectOnboarding(true);
    setConnectError('');
    try {
      const result = await awsApi.connectOnboard();
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else {
        setConnectError(result.error || 'Failed to start onboarding');
        setConnectOnboarding(false);
      }
    } catch (err) {
      setConnectError(err.message);
      setConnectOnboarding(false);
    }
  };

  const handleConnectDashboard = async () => {
    setConnectDashboardLoading(true);
    setConnectError('');
    try {
      const result = await awsApi.connectDashboard();
      if (result.success && result.data?.url) {
        window.open(result.data.url, '_blank');
      } else {
        setConnectError(result.error || 'Failed to open dashboard');
      }
    } catch (err) {
      setConnectError(err.message);
    } finally { setConnectDashboardLoading(false); }
  };

  const handleConnectDisconnect = async () => {
    if (!window.confirm('Disconnect your bank account? Patients will no longer be able to pay online.')) return;
    try {
      await awsApi.connectDisconnect();
      setConnectStatus({ connected: false });
      setConnectSuccess('Bank account disconnected.');
      setTimeout(() => setConnectSuccess(''), 3000);
    } catch (err) {
      setConnectError(err.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <SettingsIcon size={32} style={{ color: '#0d9488' }} />
              <div>
                <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' }}>Settings</h1>
                <p style={{ color: '#6b7280' }}>{practice?.name}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                color: '#374151',
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
          {/* Sidebar */}
          <div>
            <nav style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '8px' }}>
              <button onClick={() => setActiveSection('profile')} style={{
                width: '100%', textAlign: 'left', padding: '12px 16px', borderRadius: '8px',
                backgroundColor: activeSection === 'profile' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'profile' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'profile' ? '600' : '500',
                display: 'flex', alignItems: 'center', gap: '12px',
                border: 'none', cursor: 'pointer', marginTop: '4px'
              }}>
                <Building2 size={20} />
                Practice Profile
              </button>

              <button onClick={() => setActiveSection('pms')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'pms' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'pms' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'pms' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer'
              }}>
                <Database size={20} />
                PMS Integration
              </button>

              {isOrgUser && (
                <button onClick={() => setActiveSection('organization')} style={{
                  width: '100%', textAlign: 'left', padding: '12px 16px', borderRadius: '8px',
                  backgroundColor: activeSection === 'organization' ? '#f0fdfa' : 'transparent',
                  color: activeSection === 'organization' ? '#0d9488' : '#374151',
                  fontWeight: activeSection === 'organization' ? '600' : '500',
                  display: 'flex', alignItems: 'center', gap: '12px',
                  border: 'none', cursor: 'pointer', marginTop: '4px'
                }}>
                  <Building2 size={20} />
                  Organization
                </button>
              )}

              <button onClick={() => setActiveSection('clearinghouse')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'clearinghouse' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'clearinghouse' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'clearinghouse' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Wifi size={20} />
                Clearinghouse
              </button>

              <button onClick={() => setActiveSection('banking')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'banking' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'banking' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'banking' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Landmark size={20} />
                Bank Deposits
              </button>

              <button onClick={() => setActiveSection('automation')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'automation' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'automation' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'automation' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Zap size={20} />
                Automation
              </button>

              <button onClick={() => setActiveSection('exceptions')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'exceptions' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'exceptions' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'exceptions' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Shield size={20} />
                Billing Exceptions
              </button>

              <button onClick={() => setActiveSection('payers')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'payers' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'payers' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'payers' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Phone size={20} />
                Payers
              </button>

              <button onClick={() => setActiveSection('integrations')} style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: activeSection === 'integrations' ? '#f0fdfa' : 'transparent',
                color: activeSection === 'integrations' ? '#0d9488' : '#374151',
                fontWeight: activeSection === 'integrations' ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                marginTop: '4px'
              }}>
                <Link size={20} />
                Integrations
              </button>

              {practice?.role !== 'staff' && (
              <button
                onClick={() => navigate('/user-management')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  color: '#374151',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '4px',
                  backgroundColor: 'transparent'
                }}
              >
                <Users size={20} />
                User Management
              </button>
              )}

              {practice?.role !== 'staff' && (
              <button
                onClick={() => navigate('/billing')}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  color: '#374151',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '4px',
                  backgroundColor: 'transparent'
                }}
              >
                <CreditCard size={20} />
                Billing & Subscription
              </button>
              )}
            </nav>

            {/* Practice Info Card */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px', marginTop: '24px' }}>
              <h3 style={{ fontWeight: '600', marginBottom: '16px' }}>Practice Information</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '4px' }}>Practice Name</div>
                  <div style={{ fontWeight: '500' }}>{practice?.name}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontWeight: '500' }}>{user?.email || 'Not set'}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', marginBottom: '4px' }}>Status</div>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    backgroundColor: '#d1fae5',
                    color: '#065f46',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Panel */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px' }}>

            {/* ── ORGANIZATION ── */}
            {activeSection === 'organization' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Building2 size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Organization</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                  Manage the practices your organization services and your team members.
                </p>

                {orgSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {orgSuccess}
                  </div>
                )}
                {orgError && (
                  <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
                    {orgError}
                  </div>
                )}

                {orgLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Org Info */}
                    {org && (
                      <div style={{ padding: '16px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                        <div style={{ fontSize: '13px', color: '#0d9488', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: 1 }}>
                          {org.org_type === 'dso' ? 'DSO' : 'Billing Company'}
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f766e' }}>{org.name}</div>
                        {org.contact_email && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{org.contact_email}</div>}
                      </div>
                    )}

                    {/* Practices */}
                    <div data-tour="org-practices">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontWeight: '600', fontSize: '16px' }}>Managed Practices ({orgPractices.length})</h3>
                        <button
                          onClick={() => setShowAddPractice(p => !p)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          <Plus size={14} /> Add Practice
                        </button>
                      </div>

                      {showAddPractice && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '12px', backgroundColor: '#f9fafb' }}>
                          <h4 style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>New Practice</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Practice Name *</label>
                              <input type="text" value={newPractice.name} onChange={e => setNewPractice(p => ({ ...p, name: e.target.value }))}
                                placeholder="Smile Dental Group" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>NPI Number</label>
                              <input type="text" value={newPractice.npi} onChange={e => setNewPractice(p => ({ ...p, npi: e.target.value }))}
                                placeholder="1234567890" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Contact Email</label>
                              <input type="email" value={newPractice.contact_email} onChange={e => setNewPractice(p => ({ ...p, contact_email: e.target.value }))}
                                placeholder="doctor@practice.com" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Phone</label>
                              <input type="text" value={newPractice.phone} onChange={e => setNewPractice(p => ({ ...p, phone: e.target.value }))}
                                placeholder="(555) 123-4567" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={handleAddPractice} disabled={orgSaving}
                              style={{ padding: '8px 16px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              {orgSaving ? 'Adding...' : 'Add Practice'}
                            </button>
                            <button onClick={() => setShowAddPractice(false)}
                              style={{ padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {orgPractices.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
                            No practices added yet. Click "Add Practice" to get started.
                          </div>
                        ) : orgPractices.map(p => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>{p.name}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {p.npi && `NPI: ${p.npi}`}{p.npi && p.contact_email && ' · '}{p.contact_email}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {p.clearinghouse_config && (
                                <span style={{ fontSize: '11px', backgroundColor: '#f0fdfa', color: '#0d9488', padding: '2px 8px', borderRadius: '12px', border: '1px solid #99f6e4', fontWeight: 600 }}>
                                  Clearinghouse ✓
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Invite Team Member */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <h3 style={{ fontWeight: '600', fontSize: '16px' }}>Invite Team Member</h3>
                        <button
                          onClick={() => setShowInviteUser(p => !p)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          <Mail size={14} /> Invite User
                        </button>
                      </div>

                      {showInviteUser && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', backgroundColor: '#f9fafb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Email Address *</label>
                              <input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                                placeholder="colleague@company.com" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Role</label>
                              <select value={inviteForm.invited_role} onChange={e => setInviteForm(p => ({ ...p, invited_role: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: 'white' }}>
                                <option value="staff">Staff</option>
                                <option value="admin">Admin</option>
                                <option value="billing_admin">Billing Admin</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Assign to Practice</label>
                              <select value={inviteForm.practice_id} onChange={e => setInviteForm(p => ({ ...p, practice_id: e.target.value }))}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: 'white' }}>
                                <option value="">All practices</option>
                                {orgPractices.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={handleInviteUser} disabled={orgSaving}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              <Mail size={13} />{orgSaving ? 'Sending...' : 'Send Invitation'}
                            </button>
                            <button onClick={() => setShowInviteUser(false)}
                              style={{ padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </div>
                          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                            They'll receive an email with a link to sign up. Their account will be pre-configured for your organization.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── PRACTICE PROFILE ── */}
            {activeSection === 'profile' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Building2 size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Practice Profile</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                  Practice name, NPI, EIN, and address used on insurance claims.
                </p>
                {profileSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {profileSuccess}
                  </div>
                )}
                {profileError && (
                  <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
                    {profileError}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Practice Name</label>
                    <input type="text" value={profileForm.name || practice?.name || ''} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={practice?.name || 'Practice Name'}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>NPI Number <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="text" value={profileForm.npi} onChange={e => setProfileForm(f => ({ ...f, npi: e.target.value }))}
                      placeholder="1234567890"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>10-digit NPI — required on all claims</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>EIN / Tax ID <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="text" value={profileForm.ein} onChange={e => setProfileForm(f => ({ ...f, ein: e.target.value }))}
                      placeholder="12-3456789"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Federal tax ID for billing</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Phone</label>
                    <input type="text" value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Street Address</label>
                    <input type="text" value={profileForm.address} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="123 Main St"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>City</label>
                    <input type="text" value={profileForm.city} onChange={e => setProfileForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="Detroit"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>State</label>
                      <input type="text" value={profileForm.state} onChange={e => setProfileForm(f => ({ ...f, state: e.target.value }))}
                        placeholder="MI" maxLength={2}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>ZIP</label>
                      <input type="text" value={profileForm.zip} onChange={e => setProfileForm(f => ({ ...f, zip: e.target.value }))}
                        placeholder="48201"
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setProfileSaving(true); setProfileSuccess(''); setProfileError('');
                    try {
                      const res = await awsApi.updatePractice(profileForm);
                      if (res.error) throw new Error(res.error);
                      setProfileSuccess('Practice profile saved successfully.');
                      setProfileForm(f => ({ ...f }));
                    } catch (err) {
                      setProfileError(err.message || 'Failed to save practice profile.');
                    } finally {
                      setProfileSaving(false);
                    }
                  }}
                  disabled={profileSaving}
                  style={{ marginTop: '20px', padding: '10px 24px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  {profileSaving ? 'Saving...' : 'Save Profile'}
                </button>
              </>
            )}

            {/* ── CLEARINGHOUSE SETTINGS ── */}
            {activeSection === 'clearinghouse' && (
              <div data-tour="clearinghouse-settings">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Wifi size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Clearinghouse Settings</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                  Configure SFTP credentials for submitting claims to your clearinghouse. Works with Office Ally, Vyne, RemitLite, or any SFTP-based clearinghouse.
                </p>

                {chSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {chSuccess}
                  </div>
                )}
                {chError && (
                  <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
                    {chError}
                  </div>
                )}
                {chTestResult && (
                  <div style={{
                    backgroundColor: chTestResult.success ? '#f0fdf4' : '#fef2f2',
                    border: chTestResult.success ? '1px solid #bbf7d0' : '1px solid #fecaca',
                    borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
                    display: 'flex', alignItems: 'center', gap: '12px'
                  }}>
                    {chTestResult.success ? <CheckCircle size={20} style={{ color: '#16a34a' }} /> : <XCircle size={20} style={{ color: '#dc2626' }} />}
                    <span style={{ color: chTestResult.success ? '#166534' : '#991b1b' }}>{chTestResult.message}</span>
                  </div>
                )}

                {chLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading settings...</div>
                ) : (<>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* Clearinghouse dropdown */}
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Clearinghouse Provider</label>
                      <select
                        value={chConfig.clearinghouse}
                        onChange={e => {
                          const val = e.target.value;
                          const defaults = {
                            office_ally: { host: 'ftp10.officeally.com', port: '22' },
                            dentalxchange: { host: 'sftp.dentalxchange.com', port: '22' },
                            change_healthcare: { host: 'sftp.changehealthcare.com', port: '22' },
                            waystar: { host: 'sftp.waystar.com', port: '22' },
                            vyne: { host: '', port: '22' },
                            renaissance: { host: '', port: '22' },
                            other: { host: '', port: '22' },
                            stedi: { host: 'stedi', port: '22', type: 'stedi' },
                          };
                          setChConfig(prev => ({ ...prev, clearinghouse: val, ...defaults[val] }));
                        }}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white' }}
                      >
                        <option value="stedi">Stedi (Sandbox/API)</option>
                        <option value="office_ally">Office Ally</option>
                        <option value="dentalxchange">DentalXChange (Dentrix eClaims)</option>
                        <option value="change_healthcare">Change Healthcare (Optum)</option>
                        <option value="waystar">Waystar</option>
                        <option value="vyne">Vyne Dental</option>
                        <option value="renaissance">Renaissance (Remote Lite)</option>
                        <option value="other">Other (Custom SFTP)</option>
                      </select>
                    </div>

                    {/* SFTP Host */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>SFTP Host</label>
                        <input
                          type="text"
                          value={chConfig.host}
                          onChange={e => setChConfig(prev => ({ ...prev, host: e.target.value }))}
                          placeholder="ftp10.officeally.com"
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Port</label>
                        <input
                          type="number"
                          value={chConfig.port}
                          onChange={e => setChConfig(prev => ({ ...prev, port: e.target.value }))}
                          placeholder="22"
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>SFTP Username</label>
                      <input
                        type="text"
                        value={chConfig.username}
                        onChange={e => setChConfig(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="Your SFTP username"
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>SFTP Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showChPassword ? 'text' : 'password'}
                          value={chConfig.password}
                          onChange={e => setChConfig(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Your SFTP password"
                          style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowChPassword(p => !p)}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                        >
                          {showChPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Upload directory */}
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Upload Directory <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                      <input
                        type="text"
                        value={chConfig.upload_directory}
                        onChange={e => setChConfig(prev => ({ ...prev, upload_directory: e.target.value }))}
                        placeholder="Leave blank to auto-detect"
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>e.g. /claims — if left blank, FlowRCM will auto-detect the upload folder</p>
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        type="button"
                        onClick={handleTestClearinghouse}
                        disabled={chTesting}
                        style={{
                          padding: '12px 20px', border: '1px solid #d1d5db', color: '#374151',
                          borderRadius: '8px', fontWeight: '600', cursor: chTesting ? 'not-allowed' : 'pointer',
                          backgroundColor: 'white', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                      >
                        <Wifi size={16} />
                        {chTesting ? 'Testing...' : 'Test Connection'}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveClearinghouse}
                        disabled={chSaving}
                        style={{
                          flex: 1, backgroundColor: chSaving ? '#9ca3af' : '#0d9488',
                          color: 'white', padding: '12px', borderRadius: '8px',
                          fontWeight: '600', border: 'none', cursor: chSaving ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {chSaving ? 'Saving...' : 'Save Clearinghouse Settings'}
                      </button>
                    </div>

                    <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b' }}>
                      <strong style={{ color: '#374151' }}>Note:</strong> Credentials are stored encrypted in your practice record. Once saved, FlowRCM will use these settings when submitting claims — any billing company managing your practice can use this same configuration.
                    </div>
                  </div>

                  {/* Availity Portal Appeals */}
                  <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <Shield size={20} style={{ color: '#0d9488' }} />
                      <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Availity Portal Appeals</h3>
                      <span style={{ backgroundColor: '#f0fdfa', color: '#0d9488', border: '1px solid #99f6e4', borderRadius: '12px', padding: '2px 10px', fontSize: '12px', fontWeight: '600' }}>Optional</span>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
                      Connect your Availity account to enable automated portal appeal submissions for payers like Cigna, Aetna, United Healthcare, Humana, and Anthem. Availity is free for providers — <a href="https://www.availity.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0d9488' }}>sign up at availity.com</a>.
                    </p>

                    {availitySuccess && (
                      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={16} /> {availitySuccess}
                      </div>
                    )}
                    {availityError && (
                      <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
                        {availityError}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Availity Username</label>
                        <input
                          type="text"
                          value={availityConfig.username}
                          onChange={e => setAvailityConfig(prev => ({ ...prev, username: e.target.value }))}
                          placeholder="Your Availity username"
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Availity Password</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showAvailityPassword ? 'text' : 'password'}
                            value={availityConfig.password}
                            onChange={e => setAvailityConfig(prev => ({ ...prev, password: e.target.value }))}
                            placeholder="Your Availity password"
                            style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                          />
                          <button type="button" onClick={() => setShowAvailityPassword(p => !p)}
                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                            {showAvailityPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveAvailityConfig}
                      disabled={availitySaving || !availityConfig.username || !availityConfig.password}
                      style={{
                        backgroundColor: (availitySaving || !availityConfig.username || !availityConfig.password) ? '#9ca3af' : '#0d9488',
                        color: 'white', padding: '12px 24px', borderRadius: '8px',
                        fontWeight: '600', border: 'none', cursor: 'pointer'
                      }}
                    >
                      {availitySaving ? 'Saving...' : 'Save Availity Credentials'}
                    </button>

                    <div style={{ marginTop: '12px', padding: '12px 16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', color: '#92400e' }}>
                      <strong>How it works:</strong> When FlowRCM detects a denial from a portal-only payer, it automatically logs into Availity using these credentials and submits the appeal letter on your behalf. No manual action needed.
                    </div>
                  </div>
                </>)}
              </div>
            )}

            {/* ── INTEGRATIONS ── */}
            {activeSection === 'integrations' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Link size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Integrations</h2>
                </div>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Connect FlowRCM to your Practice Management System via API.</p>

                {/* Open Dental */}
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', background: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Database size={20} style={{ color: '#16a34a' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>Open Dental</div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>REST API — patients, claims, insurance plans</div>
                      </div>
                    </div>
                    {odSaved && (
                      <span style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '600' }}>
                        ✓ Connected
                      </span>
                    )}
                  </div>

                  {odLastSync && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                      Last synced: {new Date(odLastSync).toLocaleString()}
                    </div>
                  )}

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                      Customer API Key
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input
                          type={showOdKey ? 'text' : 'password'}
                          value={odKey}
                          onChange={e => setOdKey(e.target.value)}
                          placeholder={odSaved ? '••••••••••••••••••••••••••' : 'Paste your Open Dental Customer API Key'}
                          style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 40px 10px 12px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                        <button onClick={() => setShowOdKey(v => !v)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                          {showOdKey ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <button onClick={handleOdSave} disabled={!odKey.trim() || odSaving} style={{ background: '#0d9488', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '14px', fontWeight: '600', cursor: odKey.trim() ? 'pointer' : 'not-allowed', opacity: odKey.trim() ? 1 : 0.5, whiteSpace: 'nowrap' }}>
                        {odSaving ? 'Saving...' : 'Save Key'}
                      </button>
                    </div>
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
                      Generate this key in Open Dental → Setup → Advanced Setup → API, then paste it here.
                    </p>
                    {odSaveError && <div style={{ fontSize: '13px', color: '#dc2626', marginTop: '6px' }}>{odSaveError}</div>}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={handleOdTest} disabled={odTesting || (!odKey.trim() && !odSaved)} style={{ background: '#f8fafc', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {odTesting ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wifi size={14} />}
                      {odTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                    {odSaved && (
                      <button onClick={handleOdSync} disabled={odSyncing} style={{ background: '#0d9488', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {odSyncing ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
                        {odSyncing ? 'Syncing...' : 'Sync Now'}
                      </button>
                    )}
                  </div>

                  {odTestResult && (
                    <div style={{ marginTop: '12px', padding: '12px 16px', borderRadius: '8px', background: odTestResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${odTestResult.success ? '#bbf7d0' : '#fecaca'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: odTestResult.success ? '#16a34a' : '#dc2626' }}>
                        {odTestResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                        {odTestResult.success ? `Connected — ${odTestResult.patientCount || 'multiple'} patients found` : odTestResult.message || odTestResult.error || 'Connection failed'}
                      </div>
                    </div>
                  )}

                  {odSyncResult && (
                    <div style={{ marginTop: '12px', padding: '12px 16px', borderRadius: '8px', background: odSyncResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${odSyncResult.success ? '#bbf7d0' : '#fecaca'}` }}>
                      <div style={{ fontWeight: '600', color: odSyncResult.success ? '#16a34a' : '#dc2626', fontSize: '14px', marginBottom: '4px' }}>
                        {odSyncResult.success ? 'Sync complete' : 'Sync failed'}
                      </div>
                      {odSyncResult.success && (
                        <div style={{ fontSize: '13px', color: '#64748b' }}>
                          {odSyncResult.patients} patients · {odSyncResult.claims} claims imported
                          {odSyncResult.errors?.length > 0 && ` · ${odSyncResult.errors.length} errors`}
                          {odSyncResult.errors?.length > 0 && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', fontFamily: 'monospace' }}>{odSyncResult.errors[0]}</div>}
                        </div>
                      )}
                      {!odSyncResult.success && <div style={{ fontSize: '13px', color: '#dc2626' }}>{odSyncResult.error}</div>}
                    </div>
                  )}
                </div>

                {/* More integrations coming */}
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                  <Database size={24} style={{ color: '#94a3b8', marginBottom: '8px' }} />
                  <div style={{ fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>More integrations coming soon</div>
                  <div style={{ fontSize: '13px', color: '#94a3b8' }}>Dentrix Ascend, Eaglesoft, Carestream, WaveOrtho, Curve Dental</div>
                </div>
              </div>
            )}

            {/* ── BANK DEPOSITS ── */}
            {activeSection === 'banking' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Landmark size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Bank Deposits</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '28px' }}>
                  Connect your bank account so patients can pay balances online. Funds deposit directly into your practice's bank account automatically. No platform fees — standard Stripe processing rates apply.
                </p>

                {connectSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {connectSuccess}
                  </div>
                )}
                {connectError && (
                  <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertCircle size={16} /> {connectError}
                  </div>
                )}

                {connectLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Checking account status...</div>
                ) : connectStatus?.connected ? (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                      <div style={{ width: '48px', height: '48px', backgroundColor: '#f0fdf4', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircle size={24} style={{ color: '#16a34a' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>Bank Account Connected</div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {connectStatus.display_name || 'Your practice'}
                          {connectStatus.charges_enabled ? ' · Accepting payments' : ' · Setup incomplete'}
                          {connectStatus.payouts_enabled ? ' · Payouts active' : ' · Payouts pending'}
                        </div>
                      </div>
                    </div>

                    {!connectStatus.details_submitted && (
                      <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
                        <strong>Action needed:</strong> Complete your Stripe onboarding to start accepting payments.
                        <button onClick={handleConnectOnboard} style={{ marginLeft: '12px', color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' }}>
                          Continue setup
                        </button>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleConnectDashboard}
                        disabled={connectDashboardLoading}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#0d9488', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                      >
                        <ExternalLink size={15} />
                        {connectDashboardLoading ? 'Opening...' : 'Stripe Dashboard'}
                      </button>
                      <button
                        onClick={handleConnectDisconnect}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white', color: '#ef4444', padding: '10px 20px', borderRadius: '8px', border: '1px solid #fecaca', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '24px' }}>
                      <div style={{ width: '48px', height: '48px', backgroundColor: '#f0fdfa', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Landmark size={24} style={{ color: '#0d9488' }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '6px' }}>Connect Your Bank Account</div>
                        <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                          Powered by Stripe Connect. You'll be taken to a secure Stripe page to enter your bank account details. Setup takes about 5 minutes. FlowRCM never sees your banking credentials.
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
                      {[
                        { icon: '💳', title: 'Patients pay online', desc: 'Send a payment link via email — patients click and pay by card in seconds' },
                        { icon: '🏦', title: 'Direct deposit', desc: 'Money deposits to your bank account automatically, typically within 2 business days' },
                        { icon: '🔒', title: 'Secure & compliant', desc: 'Stripe handles all PCI compliance — no card data ever touches FlowRCM' },
                        { icon: '📊', title: 'Full reporting', desc: 'View transactions, payouts, and disputes in your Stripe Express dashboard' },
                      ].map(f => (
                        <div key={f.title} style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                          <div style={{ fontSize: '20px', marginBottom: '6px' }}>{f.icon}</div>
                          <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{f.title}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{f.desc}</div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleConnectOnboard}
                      disabled={connectOnboarding}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: connectOnboarding ? '#9ca3af' : '#0d9488', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: connectOnboarding ? 'default' : 'pointer', fontWeight: '600', fontSize: '15px' }}
                    >
                      <Landmark size={16} />
                      {connectOnboarding ? 'Redirecting to Stripe...' : 'Connect Bank Account'}
                    </button>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
                      You'll be redirected to Stripe to complete setup. Takes ~5 minutes.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── AUTOMATION SETTINGS ── */}
            {activeSection === 'automation' && (
              <div data-tour="automation-settings">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Zap size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Automation Settings</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                  FlowRCM runs automatically every 4 hours. Configure what actions it takes on your behalf.
                </p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '6px', padding: '6px 12px', marginBottom: '28px', fontSize: '13px', color: '#0d9488' }}>
                  <Clock size={14} />
                  Runs every 4 hours — next run is automatic
                </div>

                {automationSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {automationSuccess}
                  </div>
                )}

                {automationLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading settings...</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Auto-Appeal Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '8px', flexShrink: 0 }}>
                            <Shield size={20} style={{ color: '#d97706' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Auto-Appeal Denied Claims</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              When a claim is marked Denied, FlowRCM automatically generates a professional appeal letter using AI and files it. Practice receives an email summary.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.autoAppealEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, autoAppealEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.autoAppealEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', content: '', height: '20px', width: '20px',
                              left: automationSettings.autoAppealEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Payment Reminders Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#ede9fe', borderRadius: '8px', flexShrink: 0 }}>
                            <Bell size={20} style={{ color: '#7c3aed' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Patient Payment Reminders</div>
                            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                              Automatically email patients with outstanding balances. Sends at 7, 30, and 60 days. Professional templates, HIPAA compliant.
                            </div>
                            {automationSettings.remindersEnabled && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>Minimum balance:</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '13px', color: '#6b7280' }}>$</span>
                                  <input
                                    type="number"
                                    min="1"
                                    value={automationSettings.minBalance}
                                    onChange={e => setAutomationSettings(p => ({ ...p, minBalance: parseInt(e.target.value) || 5 }))}
                                    style={{ width: '72px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                                  />
                                </div>
                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Patients below this amount won't receive reminders</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.remindersEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, remindersEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.remindersEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.remindersEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Claim Aging Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '8px', flexShrink: 0 }}>
                            <Clock size={20} style={{ color: '#b45309' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Claim Aging Alerts</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              Automatically flag claims that have been pending over 30 days. Shows on your dashboard with priority levels (Medium / High / Urgent).
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.agingFlagEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, agingFlagEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.agingFlagEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.agingFlagEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Automation Email Summary Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '8px', flexShrink: 0 }}>
                            <Mail size={20} style={{ color: '#0284c7' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Automation Email Summary</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              Receive an email summary after each automation run showing appeals filed, aging claims flagged, ERAs posted, and any exceptions requiring attention.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.automationEmailEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, automationEmailEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.automationEmailEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.automationEmailEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* ERA Posting Mode */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        <div style={{ padding: '8px', backgroundColor: '#ede9fe', borderRadius: '8px', flexShrink: 0 }}>
                          <RefreshCw size={20} style={{ color: '#7c3aed' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>ERA Posting Mode</div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>
                            Control which ERA line items are auto-posted when FlowRCM syncs from your clearinghouse.
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {[
                              { value: 'full', label: 'Full posting', desc: 'Auto-post all ERA line items — payments, denials, and adjustments' },
                              { value: 'non_payments_only', label: 'Non-payments only', desc: 'Post only zero-paid and denied lines — review full payments manually' },
                              { value: 'denials_only', label: 'Denials only', desc: 'Post only denied claims — all payments require manual review' },
                            ].map(opt => (
                              <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="eraPostingMode"
                                  value={opt.value}
                                  checked={automationSettings.eraPostingMode === opt.value}
                                  onChange={() => setAutomationSettings(p => ({ ...p, eraPostingMode: opt.value }))}
                                  style={{ marginTop: '3px', accentColor: '#0d9488' }}
                                />
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{opt.label}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{opt.desc}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pred Auto-Submit Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#ecfdf5', borderRadius: '8px', flexShrink: 0 }}>
                            <Zap size={20} style={{ color: '#059669' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Auto-Submit Predeterminations</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              Automatically submit predetermination requests to your clearinghouse via EDI 278 when new preds are detected. Responses are pulled automatically and uploaded to the patient chart.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.autoSubmitPreauthEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, autoSubmitPreauthEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.autoSubmitPreauthEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.autoSubmitPreauthEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Benefits Verification Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#eff6ff', borderRadius: '8px', flexShrink: 0 }}>
                            <Shield size={20} style={{ color: '#2563eb' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Benefits Verification (pVerify)</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              Automatically verify patient insurance eligibility before claim submission. Disable for practices that handle eligibility checks in their PMS.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.benefitsVerificationEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, benefitsVerificationEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.benefitsVerificationEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.benefitsVerificationEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Claim Robot Toggle */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '8px', flexShrink: 0 }}>
                            <Zap size={20} style={{ color: '#d97706' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Claim Robot Auto-Scrub</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              Automatically scrub every claim for coding errors, missing fields, and denial risk when it enters the system. Disable only if your team prefers manual review before submission.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.claimRobotEnabled}
                            onChange={e => setAutomationSettings(p => ({ ...p, claimRobotEnabled: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.claimRobotEnabled ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.claimRobotEnabled ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3f'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Pred Workflow Mode */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        <div style={{ padding: '8px', backgroundColor: '#ecfdf5', borderRadius: '8px', flexShrink: 0 }}>
                          <RefreshCw size={20} style={{ color: '#059669' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Predetermination Workflow Mode</div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>
                            Control how FlowRCM handles predeterminations detected from your practice management system.
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {[
                              { value: 'submit_and_pull', label: 'Submit & Pull', desc: 'Auto-submit via EDI 278 and automatically pull responses when received' },
                              { value: 'submit_only', label: 'Submit only', desc: 'Auto-submit preds but require manual review of responses' },
                              { value: 'pull_only', label: 'Pull only', desc: 'Manually submit preds but auto-pull and upload responses' },
                              { value: 'manual', label: 'Manual', desc: 'Handle all predetermination steps manually — FlowRCM tracks status only' },
                            ].map(opt => (
                              <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="predWorkflowMode"
                                  value={opt.value}
                                  checked={automationSettings.predWorkflowMode === opt.value}
                                  onChange={() => setAutomationSettings(p => ({ ...p, predWorkflowMode: opt.value }))}
                                  style={{ marginTop: '3px', accentColor: '#0d9488' }}
                                />
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{opt.label}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{opt.desc}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Auto Patient Payment Links */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <div style={{ padding: '8px', backgroundColor: '#ecfdf5', borderRadius: '8px', flexShrink: 0 }}>
                            <CreditCard size={20} style={{ color: '#059669' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Auto Patient Payment Links</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              When an ERA payment is posted and a patient balance remains, automatically email the patient a secure payment link — accepts card, bank transfer, HSA, or FSA. Requires Stripe Connect to be configured.
                            </div>
                          </div>
                        </div>
                        <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
                          <input type="checkbox" checked={automationSettings.autoPatientPaymentLinks}
                            onChange={e => setAutomationSettings(p => ({ ...p, autoPatientPaymentLinks: e.target.checked }))}
                            style={{ opacity: 0, width: 0, height: 0 }} />
                          <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: automationSettings.autoPatientPaymentLinks ? '#0d9488' : '#d1d5db',
                            borderRadius: '26px', transition: '0.3s'
                          }}>
                            <span style={{
                              position: 'absolute', height: '20px', width: '20px',
                              left: automationSettings.autoPatientPaymentLinks ? '24px' : '3px', bottom: '3px',
                              backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={handleSaveAutomation}
                      disabled={automationSaving}
                      style={{
                        width: '100%', padding: '14px',
                        backgroundColor: automationSaving ? '#9ca3af' : '#0d9488',
                        color: 'white', border: 'none', borderRadius: '8px',
                        fontWeight: '600', fontSize: '15px',
                        cursor: automationSaving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {automationSaving ? 'Saving...' : 'Save Automation Settings'}
                    </button>

                    <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b' }}>
                      <strong style={{ color: '#374151' }}>How it works:</strong> FlowRCM runs every 4 hours via AWS EventBridge. Each run checks for denied claims to appeal, aging claims to flag, and patients due for a payment reminder. All actions are logged in the Automation Activity feed on your dashboard.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── BILLING EXCEPTIONS ── */}
            {activeSection === 'exceptions' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Shield size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Billing Exceptions</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '28px' }}>
                  Define procedure codes and scrubber flags that should be suppressed for your practice. For example, if you routinely bill D9230 twice in the same visit, add it here so the claim scrubber won't flag it.
                </p>

                {excSuccess && (
                  <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> {excSuccess}
                  </div>
                )}
                {excError && (
                  <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px' }}>
                    {excError}
                  </div>
                )}

                {excLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* Allowed Duplicate Codes */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>Allowed Duplicate Procedure Codes</div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                        Procedure codes listed here will not trigger a "duplicate code" flag during claim scrubbing.
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {(exceptions.allowedDuplicateCodes || []).map(code => (
                          <div key={code} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '6px', padding: '4px 10px', fontSize: '13px', color: '#0d9488' }}>
                            {code}
                            <button onClick={() => setExceptions(p => ({ ...p, allowedDuplicateCodes: p.allowedDuplicateCodes.filter(c => c !== code) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0', lineHeight: 1 }}>
                              <XCircle size={14} />
                            </button>
                          </div>
                        ))}
                        {(exceptions.allowedDuplicateCodes || []).length === 0 && (
                          <span style={{ fontSize: '13px', color: '#9ca3af' }}>No codes added yet</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="e.g. D9230"
                          value={newDupCode}
                          onChange={e => setNewDupCode(e.target.value.toUpperCase())}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newDupCode.trim()) {
                              setExceptions(p => ({ ...p, allowedDuplicateCodes: [...new Set([...p.allowedDuplicateCodes, newDupCode.trim()])] }));
                              setNewDupCode('');
                            }
                          }}
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <button
                          onClick={() => {
                            if (newDupCode.trim()) {
                              setExceptions(p => ({ ...p, allowedDuplicateCodes: [...new Set([...p.allowedDuplicateCodes, newDupCode.trim()])] }));
                              setNewDupCode('');
                            }
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                        >
                          <Plus size={16} /> Add
                        </button>
                      </div>
                    </div>

                    {/* Suppress Flag Codes */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>Suppress Scrubber Flags</div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                        Suppress specific AI scrubber flag codes (e.g. <code>MISSING_AUTH</code>, <code>DUPLICATE_CODE</code>) that are false positives for your practice.
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {(exceptions.suppressFlags || []).map(flag => (
                          <div key={flag} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', padding: '4px 10px', fontSize: '13px', color: '#92400e' }}>
                            {flag}
                            <button onClick={() => setExceptions(p => ({ ...p, suppressFlags: p.suppressFlags.filter(f => f !== flag) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0', lineHeight: 1 }}>
                              <XCircle size={14} />
                            </button>
                          </div>
                        ))}
                        {(exceptions.suppressFlags || []).length === 0 && (
                          <span style={{ fontSize: '13px', color: '#9ca3af' }}>No flags suppressed</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="e.g. MISSING_AUTH"
                          value={newSuppressFlag}
                          onChange={e => setNewSuppressFlag(e.target.value.toUpperCase())}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && newSuppressFlag.trim()) {
                              setExceptions(p => ({ ...p, suppressFlags: [...new Set([...p.suppressFlags, newSuppressFlag.trim()])] }));
                              setNewSuppressFlag('');
                            }
                          }}
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <button
                          onClick={() => {
                            if (newSuppressFlag.trim()) {
                              setExceptions(p => ({ ...p, suppressFlags: [...new Set([...p.suppressFlags, newSuppressFlag.trim()])] }));
                              setNewSuppressFlag('');
                            }
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#0d9488', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                        >
                          <Plus size={16} /> Add
                        </button>
                      </div>
                    </div>

                    {/* Custom Notes */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>Notes</div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                        Internal notes about your exception rules (not used in scrubbing).
                      </div>
                      <textarea
                        value={exceptions.customNotes || ''}
                        onChange={e => setExceptions(p => ({ ...p, customNotes: e.target.value }))}
                        placeholder="e.g. D9230 is billed twice per visit when separate upper/lower nitrous sessions are documented."
                        rows={3}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>

                    <button
                      onClick={handleSaveExceptions}
                      disabled={excSaving}
                      style={{
                        width: '100%', padding: '14px',
                        backgroundColor: excSaving ? '#9ca3af' : '#0d9488',
                        color: 'white', border: 'none', borderRadius: '8px',
                        fontWeight: '600', fontSize: '15px',
                        cursor: excSaving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {excSaving ? 'Saving...' : 'Save Exception Rules'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── PMS INTEGRATION ── */}
            {activeSection === 'payers' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Phone size={24} style={{ color: '#0d9488' }} />
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Payers</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                  Manage payer appeal contacts. Appeals will only be sent automatically to payers you have verified. <strong>Never verify a number without confirming it directly with the payer.</strong>
                </p>
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', fontSize: '13px', color: '#9a3412', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <AlertCircle size={16} style={{ marginTop: '1px', flexShrink: 0 }} />
                  <span>Sending patient information to a wrong fax number is a HIPAA violation. Only click <strong>Verify</strong> after you have independently confirmed the number is correct.</span>
                </div>

                {payerMsg && (
                  <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#0f766e' }}>
                    {payerMsg}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                  <input
                    placeholder="Search payers..."
                    value={payerSearch}
                    onChange={e => setPayerSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadPayers()}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                  />
                  <button onClick={loadPayers} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px' }}>
                    Search
                  </button>
                  <button onClick={openAddPayer} style={{ padding: '8px 16px', borderRadius: '8px', background: '#0d9488', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Plus size={16} /> Add Payer
                  </button>
                </div>

                {payersLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}><RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
                ) : payers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    No payers found. Click <strong>Add Payer</strong> to add your first payer contact.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>PAYER</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>METHOD</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>CONTACT</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>STATUS</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payers.map((p, i) => (
                          <tr key={p.id} style={{ borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', background: p.human_verified ? 'white' : '#fffbeb' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>{p.payer_name}</div>
                              {p.payer_id && <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {p.payer_id}</div>}
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', textTransform: 'capitalize' }}>{p.appeal_method || 'fax'}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>
                              {p.appeal_method === 'fax' && p.appeal_fax && <span>{p.appeal_fax}</span>}
                              {p.appeal_method === 'email' && p.appeal_email && <span style={{ fontSize: '12px' }}>{p.appeal_email}</span>}
                              {p.appeal_method === 'portal' && p.appeal_portal_url && <span style={{ fontSize: '12px', color: '#0d9488' }}>Portal</span>}
                              {!p.appeal_fax && !p.appeal_email && !p.appeal_portal_url && <span style={{ color: '#9ca3af' }}>—</span>}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {p.human_verified ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                  <CheckCircle size={12} /> Verified
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fef9c3', color: '#a16207', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                  <AlertCircle size={12} /> Unverified
                                </span>
                              )}
                              {p.human_verified && p.human_verified_by && (
                                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>by {p.human_verified_by.split('@')[0]}</div>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={() => openEditPayer(p)} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Edit2 size={12} /> Edit
                                </button>
                                {!p.human_verified ? (
                                  <button onClick={() => setConfirmVerify(p)} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #0d9488', background: '#f0fdfa', color: '#0d9488', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <CheckCircle size={12} /> Verify
                                  </button>
                                ) : (
                                  <button onClick={() => handleUnverifyPayer(p.id)} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}>
                                    Unverify
                                  </button>
                                )}
                                <button onClick={() => handleDeletePayer(p.id)} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add/Edit Payer Modal */}
                {showPayerModal && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px' }}>
                      <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>{editingPayer ? 'Edit Payer' : 'Add Payer'}</h2>
                      {payerError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{payerError}</div>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Payer Name *</label>
                          <input value={payerForm.payer_name} onChange={e => setPayerForm(f => ({ ...f, payer_name: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="e.g. Delta Dental" />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Payer ID (EDI)</label>
                          <input value={payerForm.payer_id} onChange={e => setPayerForm(f => ({ ...f, payer_id: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="e.g. 86027" />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Appeal Method</label>
                          <select value={payerForm.appeal_method} onChange={e => setPayerForm(f => ({ ...f, appeal_method: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}>
                            <option value="fax">Fax</option>
                            <option value="email">Email</option>
                            <option value="portal">Portal</option>
                          </select>
                        </div>
                        {payerForm.appeal_method === 'fax' && (
                          <div>
                            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Appeal Fax Number</label>
                            <input value={payerForm.appeal_fax} onChange={e => setPayerForm(f => ({ ...f, appeal_fax: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="e.g. 1-800-555-1234" />
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Verify this number directly with the payer before saving.</p>
                          </div>
                        )}
                        {payerForm.appeal_method === 'email' && (
                          <div>
                            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Appeal Email</label>
                            <input value={payerForm.appeal_email} onChange={e => setPayerForm(f => ({ ...f, appeal_email: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="appeals@payer.com" />
                          </div>
                        )}
                        {payerForm.appeal_method === 'portal' && (
                          <div>
                            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Portal URL</label>
                            <input value={payerForm.appeal_portal_url} onChange={e => setPayerForm(f => ({ ...f, appeal_portal_url: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="https://provider.payer.com/appeals" />
                          </div>
                        )}
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Notes</label>
                          <input value={payerForm.appeal_notes} onChange={e => setPayerForm(f => ({ ...f, appeal_notes: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }} placeholder="Optional notes about this payer's appeal process" />
                        </div>
                        {editingPayer?.human_verified && (
                          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#9a3412' }}>
                            <AlertCircle size={13} style={{ display: 'inline', marginRight: '6px' }} />
                            Changing the fax number or email will reset verification. You will need to re-verify before auto-send resumes.
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setShowPayerModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                        <button onClick={handleSavePayer} disabled={payerSaving} style={{ padding: '10px 20px', borderRadius: '8px', background: '#0d9488', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                          {payerSaving ? 'Saving...' : 'Save Payer'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Verify Confirmation Dialog */}
                {confirmVerify && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ background: '#fef9c3', borderRadius: '50%', padding: '10px' }}>
                          <AlertCircle size={22} style={{ color: '#d97706' }} />
                        </div>
                        <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Confirm Payer Contact</h2>
                      </div>
                      <p style={{ color: '#374151', fontSize: '14px', marginBottom: '16px' }}>
                        By clicking <strong>I Confirm</strong>, you confirm that:
                      </p>
                      <ul style={{ color: '#374151', fontSize: '14px', marginBottom: '20px', paddingLeft: '20px', lineHeight: '1.8' }}>
                        <li>The {confirmVerify.appeal_method === 'fax' ? 'fax number' : confirmVerify.appeal_method === 'email' ? 'email address' : 'portal'} <strong>{confirmVerify.appeal_fax || confirmVerify.appeal_email || confirmVerify.appeal_portal_url || '—'}</strong> is the correct appeal contact for <strong>{confirmVerify.payer_name}</strong></li>
                        <li>You verified this independently with the payer</li>
                        <li>Patient information will be sent to this contact automatically for future denials</li>
                      </ul>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setConfirmVerify(null)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
                          Cancel
                        </button>
                        <button
                          onClick={() => handleVerifyPayer(confirmVerify)}
                          disabled={verifyingPayerId === confirmVerify?.id}
                          style={{ padding: '10px 20px', borderRadius: '8px', background: '#0d9488', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                        >
                          {verifyingPayerId === confirmVerify?.id ? 'Verifying...' : 'I Confirm — Verify Payer'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeSection === 'pms' && <>
            <div data-tour="pms-integration">
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>PMS Integration</h2>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              Configure how FlowRCM connects to your practice management system.
            </p>
            <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', fontSize: '14px', color: '#1e40af' }}>
              <strong>Using Open Dental?</strong> Connect via the <button onClick={() => setActiveSection('integrations')} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '14px' }}>Integrations tab</button> — FlowRCM connects directly through the Open Dental REST API.
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                color: '#166534',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                {success}
              </div>
            )}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                {error}
              </div>
            )}

            {/* Test Result */}
            {testResult && (
              <div style={{
                backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
                border: testResult.success ? '1px solid #bbf7d0' : '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {testResult.success ? (
                  <CheckCircle size={20} style={{ color: '#16a34a' }} />
                ) : (
                  <XCircle size={20} style={{ color: '#dc2626' }} />
                )}
                <span style={{ color: testResult.success ? '#166534' : '#991b1b' }}>
                  {testResult.message}
                </span>
              </div>
            )}

            <form onSubmit={handleSavePMSConfig} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* PMS System Selection */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Select Your PMS System
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {pmsSystems.map((system) => (
                    <label
                      key={system.id}
                      style={{
                        display: 'flex',
                        alignItems: 'start',
                        padding: '16px',
                        border: selectedPMS === system.id ? '2px solid #0d9488' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedPMS === system.id ? '#f0fdfa' : 'transparent'
                      }}
                    >
                      <input
                        type="radio"
                        name="pmsSystem"
                        value={system.id}
                        checked={selectedPMS === system.id}
                        onChange={(e) => setSelectedPMS(e.target.value)}
                        style={{ marginTop: '4px', marginRight: '12px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '20px' }}>{system.icon}</span>
                          <span style={{ fontWeight: '600' }}>{system.name}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                          {system.description}
                        </p>
                        {system.note && (
                          <p style={{ fontSize: '12px', color: '#059669', fontStyle: 'italic' }}>
                            {system.note}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Bot-Based PMS Credentials (WaveOrtho, Eaglesoft, Dentrix Ascend, Carestream) */}
              {BOT_PMS_SYSTEMS.includes(selectedPMS) && (
                <>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '4px' }}>
                    <p style={{ fontSize: '13px', color: '#0369a1', margin: 0 }}>
                      FlowRCM uses a secure automated agent to log in and pull your data nightly. Enter the same credentials your staff uses to log in to {selectedPMS === 'waveortho' ? 'WaveOrtho' : selectedPMS === 'eaglesoft' ? 'Eaglesoft' : selectedPMS === 'dentrix-ascend' ? 'Dentrix Ascend' : 'Carestream'}.
                    </p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Login Email / Username</label>
                    <input
                      type="text"
                      value={pmsConfig.username}
                      onChange={e => setPmsConfig(prev => ({ ...prev, username: e.target.value }))}
                      style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      placeholder="you@yourpractice.com"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Password</label>
                    <input
                      type="password"
                      value={pmsConfig.password}
                      onChange={e => setPmsConfig(prev => ({ ...prev, password: e.target.value }))}
                      style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      placeholder="Your PMS login password"
                      autoComplete="new-password"
                    />
                  </div>
                  {(selectedPMS === 'eaglesoft' || selectedPMS === 'carestream') && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Portal URL <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span></label>
                      <input
                        type="url"
                        value={pmsConfig.portalUrl || ''}
                        onChange={e => setPmsConfig(prev => ({ ...prev, portalUrl: e.target.value }))}
                        style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                        placeholder="https://your-practice.eaglesoft.com"
                      />
                      <p style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>Leave blank to use the default portal URL</p>
                    </div>
                  )}
                  {odLastSync && (
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>Last synced: {new Date(odLastSync).toLocaleString()}</p>
                  )}
                </>
              )}

              {/* Open Dental Configuration */}
              {selectedPMS === PMS_SYSTEMS.OPEN_DENTAL && (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                      Open Dental API URL
                    </label>
                    <input
                      type="url"
                      value={pmsConfig.apiUrl}
                      onChange={(e) => setPmsConfig({ ...pmsConfig, apiUrl: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="https://your-server.com:port"
                    />
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                      Your Open Dental API endpoint URL
                    </p>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                      API Key
                    </label>
                    <input
                      type="password"
                      value={pmsConfig.apiKey}
                      onChange={(e) => setPmsConfig({ ...pmsConfig, apiKey: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter your API key"
                    />
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                      Generate this in Open Dental under Setup → API
                    </p>
                  </div>
                </>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {selectedPMS === PMS_SYSTEMS.OPEN_DENTAL && (
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testing || !pmsConfig.apiUrl || !pmsConfig.apiKey}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #d1d5db',
                      color: '#374151',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: testing ? 'not-allowed' : 'pointer',
                      backgroundColor: 'white'
                    }}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    backgroundColor: loading ? '#9ca3af' : '#0d9488',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>

              {/* Sync Now — only shown when OD is saved/configured */}
              {selectedPMS === PMS_SYSTEMS.OPEN_DENTAL && pmsConfig.apiUrl && pmsConfig.apiKey && (
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', marginBottom: '2px' }}>Manual Sync</p>
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>
                        Auto-syncs every 4 hours. Last sync: {odLastSync ? new Date(odLastSync).toLocaleString() : 'Never'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSyncNow}
                      disabled={odSyncing}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        backgroundColor: odSyncing ? '#94a3b8' : '#0d9488',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: odSyncing ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      <RefreshCw size={16} style={{ animation: odSyncing ? 'spin 1s linear infinite' : 'none' }} />
                      {odSyncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                  {odSyncMessage && (
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      backgroundColor: odSyncMessage.type === 'success' ? '#f0fdfa' : '#fef2f2',
                      color: odSyncMessage.type === 'success' ? '#0f766e' : '#dc2626',
                      border: `1px solid ${odSyncMessage.type === 'success' ? '#5eead4' : '#fecaca'}`
                    }}>
                      {odSyncMessage.text}
                    </div>
                  )}
                </div>
              )}
            </form>

            {/* Go to Data Import / Documents */}
            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontWeight: '600', marginBottom: '12px' }}>Ready to Import Data?</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                Head to the Data Import page to upload CSV files or sync from your PMS.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => navigate('/data-import')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  Go to Data Import
                </button>
                <button
                  onClick={() => navigate('/documents')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  Go to Documents
                </button>
              </div>
            </div>
            </div>{/* end data-tour="pms-integration" */}

            {/* Demo Data Management - only show in demo mode */}
            {activeSection === 'pms' && (
              (process.env.REACT_APP_MODE || '').trim() === 'demo' ||
              (typeof window !== 'undefined' && window.location.hostname === 'demo.flowrcmdental.com')
            ) && (
              <div style={{
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <RefreshCw size={20} style={{ color: '#0d9488' }} />
                  <h3 style={{ fontWeight: '600' }}>Demo Data Management</h3>
                </div>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Populate your demo with realistic patient and claims data. Use "Reset & Seed" before a demo to start fresh.
                </p>

                {seedResult && (
                  <div style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <CheckCircle size={18} style={{ color: '#16a34a' }} />
                      <span style={{ fontWeight: '600', color: '#166534' }}>{seedResult.message}</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#166534' }}>
                      <p>Patients: {seedResult.patients?.created} created, {seedResult.patients?.skipped} already existed</p>
                      <p>Claims: {seedResult.claims?.created} created</p>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => handleSeedDemoData(false)}
                    disabled={seeding}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: seeding ? '#9ca3af' : '#0d9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: seeding ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {seeding ? 'Seeding...' : 'Seed Demo Data'}
                  </button>
                  {confirmReset ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <p style={{ color: '#dc2626', fontSize: '13px', fontWeight: '500' }}>
                        This will delete all existing patients and claims, then create fresh demo data.
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setConfirmReset(false); handleSeedDemoData(true); }}
                          disabled={seeding}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: seeding ? 'not-allowed' : 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Yes, Reset
                        </button>
                        <button
                          onClick={() => setConfirmReset(false)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmReset(true)}
                      disabled={seeding}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: 'white',
                        color: '#dc2626',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: seeding ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {seeding ? 'Seeding...' : 'Reset & Seed Fresh'}
                    </button>
                  )}
                </div>
                <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                  "Seed Demo Data" adds data without removing existing records. "Reset & Seed Fresh" clears everything first.
                </p>
              </div>
            )}
            </>}
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Settings;